interface ConsoleEntry {
  timestamp: number
  level: 'log' | 'warn' | 'error'
  message: string
}

interface NetworkEntry {
  timestamp: number
  method: string
  endpoint: string
  status: number | null
  durationMs: number
  error?: string
}

const MAX_CONSOLE_ENTRIES = 100
const MAX_NETWORK_ENTRIES = 50

const consoleBuffer: ConsoleEntry[] = []
const networkBuffer: NetworkEntry[] = []

let consoleInitialized = false

export function initConsoleCapture(): void {
  if (consoleInitialized) return
  consoleInitialized = true

  const levels: Array<'log' | 'warn' | 'error'> = ['log', 'warn', 'error']
  for (const level of levels) {
    const original = console[level].bind(console)
    console[level] = (...args: any[]) => {
      original(...args)
      const message = args
        .map((a) => {
          try {
            if (typeof a === 'string') return a
            if (a instanceof Error) return a.stack || a.message || String(a)
            return JSON.stringify(a)
          } catch {
            return String(a)
          }
        })
        .join(' ')
      consoleBuffer.push({ timestamp: Date.now(), level, message: message.slice(0, 500) })
      if (consoleBuffer.length > MAX_CONSOLE_ENTRIES) {
        consoleBuffer.shift()
      }
    }
  }
}

export function logNetworkRequest(entry: NetworkEntry): void {
  networkBuffer.push(entry)
  if (networkBuffer.length > MAX_NETWORK_ENTRIES) {
    networkBuffer.shift()
  }
}

export function getBufferSnapshot(): { consoleIndex: number; networkIndex: number } {
  return { consoleIndex: consoleBuffer.length, networkIndex: networkBuffer.length }
}

export function getEntriesSince(snapshot: { consoleIndex: number; networkIndex: number }): {
  console: ConsoleEntry[]
  network: NetworkEntry[]
} {
  return {
    console: consoleBuffer.slice(snapshot.consoleIndex),
    network: networkBuffer.slice(snapshot.networkIndex)
  }
}

export function getDiagnosticSnapshot(): string {
  const formatTime = (ts: number) => new Date(ts).toISOString().slice(11, 23)

  const consoleSection =
    consoleBuffer.length > 0
      ? consoleBuffer.map((e) => `[${formatTime(e.timestamp)}] ${e.level.toUpperCase()}: ${e.message}`).join('\n')
      : '(no console output captured)'

  const recentRequests = networkBuffer.slice(-5)
  const networkSection =
    recentRequests.length > 0
      ? recentRequests
          .map(
            (e) =>
              `[${formatTime(e.timestamp)}] ${e.method} ${e.endpoint} → ${e.status ?? 'N/A'} (${e.durationMs}ms)${e.error ? ' ERROR: ' + e.error : ''}`
          )
          .join('\n')
      : '(no network requests captured)'

  return `Recent console output:\n${consoleSection}\n\nRecent network requests:\n${networkSection}`
}
